import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import './driverTheme.css'
import { canUseTours } from './tourUi'
import { mountTourResumeButton, setTourResumeVisible, unmountTourResumeButton } from './tourResumeButton'
import { isTourAllowedPath, normalizeTourPath } from './tourAllowedPaths'

const DEFAULT_WAIT_TIMEOUT = 6000
const WAIT_POLL_INTERVAL = 100
const SOFT_NAV_GRACE = 1500
const RESUME_STORAGE_KEY = 'hdv_tour_resume'
const PROGRESS_STORAGE_KEY = 'hdv_tour_progress'
const MINIMIZED_STORAGE_KEY = 'hdv_tour_minimized'
const STEP_CONTEXT_STORAGE_KEY = 'hdv_demo_step_contexts'
const DEMO_SESSION_KEY = 'hdv_demo_session'
const isAutomacaoTour = (tourName) => ['novo-automacao', 'editar-automacao'].includes(tourName)
const isExportacaoTour = (tourName) => ['novo-exportacao-dados', 'editar-exportacao-dados'].includes(tourName)
const isSupervisorioTour = (tourName) => tourName === 'supervisorio'
const isModalGuardTour = (tourName) => isAutomacaoTour(tourName) || isExportacaoTour(tourName) || isSupervisorioTour(tourName)

const isExportacaoParamModalOpen = () => Boolean(
    document.querySelector('.modal.show [data-tour="exportacao-param-modal"], .modal.show [data-tour="exportacao-param-body"], .modal.show [data-tour="exportacao-param-cliente"], .modal.show .modal-large')
)

const isSupervisorioGuideModalOpen = () => Boolean(
    document.querySelector('.modal.show [data-tour="supervisorio-modal-criar"], .modal.show [data-tour="supervisorio-modal-widget"], .modal.show [data-tour="supervisorio-modal-edit"], .modal.show [data-tour="supervisorio-edit-descricao"], details.hdv-super-popup[open], .select-supervisory .rs__menu')
)
const isDemoTour = (tourName) => Boolean(tourName?.startsWith?.('demo-'))

class TourManager {
    constructor() {
        this.history = null
        this.driverObj = null
        this.tours = {}
        this.activeTourName = null
        this.finishedTourName = null
        this.lastFinishedTourName = null
        this.replacingSteps = false
        this.tourCleanup = null
        this.routeUnlisten = null
        this.resumingMinimized = false
        this.internalNavigation = false
        this.dismissReason = null
        this.tourPath = null
        this.automacaoModalObserver = null
        this.automacaoAvisoListener = null
        this.exportacaoParamModalLock = false
        this.supervisorioModalLock = false
    }

    isExportacaoParamGuideActive() {
        if (this.exportacaoParamModalLock) return true
        if (isExportacaoParamModalOpen()) return true

        const selector = String(this.getActiveStep()?.element || '')
        return selector.includes('exportacao-gerenciar-parametros') || selector.includes('exportacao-param-')
    }

    isSupervisorioGuideActive() {
        if (this.supervisorioModalLock) return true
        if (isSupervisorioGuideModalOpen()) return true

        const selector = String(this.getActiveStep()?.element || '')
        return selector.includes('supervisorio-')
    }

    setTourCleanup(cleanup) {
        this.tourCleanup = typeof cleanup === 'function' ? cleanup : null
    }

    runTourCleanup() {
        const cleanup = this.tourCleanup
        this.tourCleanup = null

        if (typeof cleanup === 'function') {
            cleanup()
        }
    }

    startAutomacaoModalGuard() {
        this.stopAutomacaoModalGuard()

        const minimizeForModal = () => {
            if (!isModalGuardTour(this.activeTourName) || !this.driverObj) return
            if (isExportacaoTour(this.activeTourName) && this.isExportacaoParamGuideActive()) return
            if (isSupervisorioTour(this.activeTourName) && this.isSupervisorioGuideActive()) return
            this.minimize()
        }

        this.automacaoAvisoListener = minimizeForModal
        document.addEventListener('showAviso', minimizeForModal)

        if (typeof MutationObserver !== 'undefined') {
            this.automacaoModalObserver = new MutationObserver(() => {
                if (isSupervisorioTour(this.activeTourName)) {
                    if (this.isSupervisorioGuideActive()) return

                    const otherModal = document.querySelector('.modal.show')
                    if (!otherModal) return

                    this.minimize()
                    return
                }

                if (isExportacaoTour(this.activeTourName)) {
                    if (this.isExportacaoParamGuideActive()) return

                    const paramModal = document.querySelector('.modal.show .modal-large, .modal.show [data-tour="exportacao-param-modal"], .modal.show [data-tour="exportacao-param-body"]')
                    if (paramModal) return

                    const otherModal = document.querySelector('.modal.show')
                    if (!otherModal) return

                    this.minimize()
                    return
                }

                if (!document.querySelector('.modal.show, .modal-backdrop.show')) return
                minimizeForModal()
            })
            this.automacaoModalObserver.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class'],
            })
        }
    }

    stopAutomacaoModalGuard() {
        if (this.automacaoAvisoListener) {
            document.removeEventListener('showAviso', this.automacaoAvisoListener)
            this.automacaoAvisoListener = null
        }

        this.automacaoModalObserver?.disconnect()
        this.automacaoModalObserver = null
    }

    isDemoSessionActive() {
        try {
            return sessionStorage.getItem(DEMO_SESSION_KEY) === '1'
        } catch (error) {
            return false
        }
    }

    currentFullPath() {
        return window.location.pathname + window.location.search + window.location.hash
    }

    getLiveActiveTabs() {
        return Array.from(
            document.querySelectorAll('.react-tabs__tab--selected[data-tour]')
        )
            .map((tab) => tab.getAttribute('data-tour'))
            .filter(Boolean)
    }

    getMinimizedState() {
        try {
            const rawMinimized = sessionStorage.getItem(MINIMIZED_STORAGE_KEY)
            return rawMinimized ? JSON.parse(rawMinimized) : null
        } catch (error) {
            return null
        }
    }

    saveMinimizedState(tourName, resumeIndex) {
        if (!tourName || isDemoTour(tourName) || !Number.isInteger(resumeIndex)) return

        try {
            sessionStorage.setItem(MINIMIZED_STORAGE_KEY, JSON.stringify({
                tourName,
                resumeIndex,
                path: this.currentFullPath(),
                activeTabs: this.getLiveActiveTabs(),
            }))
        } catch (error) {}
    }

    clearMinimizedState() {
        try {
            sessionStorage.removeItem(MINIMIZED_STORAGE_KEY)
        } catch (error) {}
    }

    syncResumeButton() {
        if (this.resumingMinimized) {
            setTourResumeVisible(false)
            return
        }

        if (!canUseTours() || this.driverObj || this.isDemoSessionActive()) {
            unmountTourResumeButton()
            return
        }

        const paused = this.getMinimizedState()
        if (!paused?.tourName || isDemoTour(paused.tourName)) {
            unmountTourResumeButton()
            return
        }

        if (!isTourAllowedPath(paused.tourName, this.currentPath(), paused.path)) {
            setTourResumeVisible(false)
            return
        }

        mountTourResumeButton(() => this.resumeMinimizedTour())
        setTourResumeVisible(true)
    }

    abandonTourIfRouteLeft() {
        if (this.internalNavigation || this.resumingMinimized || this.replacingSteps) return

        const currentPath = this.currentPath()
        const activeName = this.activeTourName

        if (activeName) {
            const origin = this.tourPath
            if (origin && normalizeTourPath(origin) !== normalizeTourPath(currentPath)) {
                this.finish(activeName)
                return
            }
        }

        const paused = this.getMinimizedState()
        if (paused?.tourName && !this.driverObj) {
            const pausedPath = normalizeTourPath(paused.path)
            if (pausedPath && pausedPath !== normalizeTourPath(currentPath)) {
                this.finish(paused.tourName)
            }
        }
    }

    async restoreActiveTabs(activeTabs) {
        for (const tourId of activeTabs || []) {
            const selector = `[data-tour="${tourId}"]`
            try {
                await this.waitForElement(selector, 2000)
            } catch (error) {}

            const tab = document.querySelector(selector)
            if (tab && !tab.classList.contains('react-tabs__tab--selected')) {
                tab.click()
            }
        }
    }

    async resumeMinimizedTour() {
        if (!canUseTours() || this.driverObj || this.isDemoSessionActive()) return

        const paused = this.getMinimizedState()
        if (!paused?.tourName || isDemoTour(paused.tourName)) {
            this.clearMinimizedState()
            unmountTourResumeButton()
            return
        }

        this.resumingMinimized = true
        setTourResumeVisible(false)

        try {
            const currentPath = this.currentFullPath()
            if (paused.path && paused.path !== currentPath) {
                if (this.history) {
                    this.history.push(paused.path)
                    await new Promise((resolve) => setTimeout(resolve, 250))
                } else {
                    window.location.href = paused.path
                    return
                }
            }

            await this.restoreActiveTabs(paused.activeTabs)

            const config = this.tours[paused.tourName]
            if (config?.readySelector) {
                try {
                    await this.waitForElement(config.readySelector, 1500)
                } catch (error) {}
            }

            await this.start(paused.tourName, {
                resumeIndex: paused.resumeIndex,
                skipReadySelector: true,
                fromMinimizedResume: true,
            })

            if (this.isActive()) {
                this.clearMinimizedState()
                unmountTourResumeButton()
            }
        } finally {
            this.resumingMinimized = false
            if (!this.isActive()) {
                this.syncResumeButton()
            }
        }
    }

    setHistory(history) {
        if (history && this.history !== history) {
            this.history = history
            if (typeof this.routeUnlisten === 'function') {
                this.routeUnlisten()
                this.routeUnlisten = null
            }
            if (typeof history.listen === 'function') {
                this.routeUnlisten = history.listen(() => {
                    this.syncResumeButton()
                    this.abandonTourIfRouteLeft()
                })
            }
        } else if (history) {
            this.history = history
        }

        this.syncResumeButton()
        this.abandonTourIfRouteLeft()
    }

    registerTour(name, config) {
        this.tours[name] = config
    }

    hasTour(name) {
        return Boolean(this.tours[name])
    }

    currentPath() {
        return window.location.pathname
    }

    getSavedProgress(name) {
        try {
            const rawProgress = sessionStorage.getItem(PROGRESS_STORAGE_KEY)
            const progress = rawProgress ? JSON.parse(rawProgress) : null

            if (!progress || progress.tourName !== name) {
                return null
            }

            if (progress.path && progress.path !== this.currentPath() && progress.path !== this.currentFullPath()) {
                return null
            }

            return progress
        } catch (error) {
            return null
        }
    }

    saveProgress(name, resumeIndex) {
        if (!Number.isInteger(resumeIndex)) return

        try {
            sessionStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify({
                tourName: name,
                resumeIndex,
                path: this.currentPath(),
            }))
        } catch (error) {}
    }

    clearSavedProgress(name) {
        try {
            const progress = this.getSavedProgress(name)

            if (progress || sessionStorage.getItem(PROGRESS_STORAGE_KEY)) {
                const rawProgress = sessionStorage.getItem(PROGRESS_STORAGE_KEY)
                const stored = rawProgress ? JSON.parse(rawProgress) : null

                if (!stored || stored.tourName === name) {
                    sessionStorage.removeItem(PROGRESS_STORAGE_KEY)
                }
            }
        } catch (error) {}
    }

    navigateAndWait(path, selector, resumeIndex, tourName) {
        const resumeTourName = tourName || this.activeTourName
        this.internalNavigation = true

        const trySoftNav = () => {
            if (!this.history) {
                return Promise.reject(new Error('history indisponível'))
            }

            this.history.push(path)

            return this.waitForElement(selector, SOFT_NAV_GRACE)
        }

        return trySoftNav()
            .then((element) => {
                this.tourPath = this.currentPath()
                return element
            })
            .finally(() => {
                this.internalNavigation = false
            })
            .catch(() => {
                this.hardNavigate(path, resumeIndex, resumeTourName)

                return new Promise(() => {})
            })
    }

    waitForElement(selector, timeout = DEFAULT_WAIT_TIMEOUT) {
        return new Promise((resolve, reject) => {
            const existing = typeof selector === 'function'
                ? selector()
                : document.querySelector(selector)

            if (existing) {
                return resolve(existing)
            }

            const start = Date.now()

            const check = () => {
                const element = typeof selector === 'function'
                    ? selector()
                    : document.querySelector(selector)

                if (element) {
                    resolve(element)
                    return
                }

                if (Date.now() - start > timeout) {
                    reject(new Error(`TourManager: elemento "${selector}" não apareceu a tempo.`))
                    return
                }

                setTimeout(check, WAIT_POLL_INTERVAL)
            }

            check()
        })
    }

    hardNavigate(path, resumeIndex, tourName) {
        try {
            sessionStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify({
                tourName: tourName || this.activeTourName,
                resumeIndex,
                path,
            }))
        } catch (error) {}

        window.location.href = path
    }

    resumeIfPending() {
        let pending = null

        try {
            const rawResume = sessionStorage.getItem(RESUME_STORAGE_KEY)

            if (rawResume) {
                pending = JSON.parse(rawResume)
            }

            sessionStorage.removeItem(RESUME_STORAGE_KEY)
        } catch (error) {}

        if (!pending || !pending.tourName) {
            this.syncResumeButton()
            return
        }

        this.start(pending.tourName, {
            resumeIndex: pending.resumeIndex,
        })
    }

    moveNext() {
        this.driverObj?.moveNext()
    }

    async movePrevious() {
        const activeIndex = this.getActiveIndex()
        const previousStep = Number.isInteger(activeIndex)
            ? this.currentSteps?.[activeIndex - 1]
            : null
        let context = previousStep?._hdvContext

        if (!context && this.activeTourName?.startsWith?.('demo-')) {
            try {
                const raw = sessionStorage.getItem(STEP_CONTEXT_STORAGE_KEY)
                const contexts = raw ? JSON.parse(raw) : {}
                context = contexts[`${this.activeTourName}:${activeIndex - 1}`]
            } catch (error) {}
        }

        if (context?.path) {
            const currentPath =
                window.location.pathname + window.location.search + window.location.hash

            if (currentPath !== context.path && this.history) {
                this.internalNavigation = true
                try {
                    this.history.push(context.path)
                    await new Promise((resolve) => setTimeout(resolve, 250))
                    this.tourPath = this.currentPath()
                } finally {
                    this.internalNavigation = false
                }
            }

            await this.restoreActiveTabs(context.activeTabs)
        }

        this.setHighlightVisible(false)

        try {
            if (typeof previousStep?.ensureReady === 'function') {
                await previousStep.ensureReady(this)
            }

            this.driverObj?.movePrevious()
        } catch (error) {
        } finally {
            this.setHighlightVisible(true)
            this.driverObj?.refresh?.()
        }
    }

    moveTo(index) {
        if (Number.isInteger(index)) {
            this.driverObj?.moveTo(index)
        }
    }

    setSteps(steps, moveToIndex) {
        const name = this.activeTourName

        if (!name || !Number.isInteger(moveToIndex)) return

        this.replacingSteps = true
        this.driverObj?.destroy()
        this.replacingSteps = false
        this.driverObj = null
        this.activeTourName = name

        this.driverObj = driver({
            showProgress: true,
            allowClose: true,
            animate: true,
            overlayOpacity: 0.65,
            overlayClickBehavior: () => {},
            stagePadding: 6,
            stageRadius: 6,
            popoverClass: 'hdv-tour-popover',
            nextBtnText: 'Próximo',
            prevBtnText: 'Voltar',
            doneBtnText: 'Concluir',
            progressText: '{{current}} de {{total}}',
            onDoneClick: (element, step, opts) => this.handleDoneClick(name, element, step, opts),
            onCloseClick: () => this.handleClose(name),
            onPrevClick: () => this.movePrevious(),
            onPopoverRender: (popover) => this.renderPopoverChrome(popover),
            onHighlighted: (highlightedElement, step, { state, driver: activeDriver }) => {
                this.saveProgress(name, state?.activeIndex)

                if (typeof step?.onHighlighted === 'function') {
                    step.onHighlighted(highlightedElement, step, { state, driver: activeDriver })
                }

                if (typeof step?.ensureReady === 'function') {
                    Promise.resolve(step.ensureReady(this))
                        .then(() => {
                            activeDriver?.refresh?.()
                            this.captureStepContext(step, state?.activeIndex)
                        })
                        .catch(() => {})
                } else {
                    this.captureStepContext(step, state?.activeIndex)
                }
            },
            onDeselected: (deselectedElement, step, deselectOptions) => {
                if (typeof step?.onDeselected === 'function') {
                    step.onDeselected(deselectedElement, step, deselectOptions)
                }
            },
            onDestroyed: (destroyedElement, destroyedStep, { state }) => {
                this.handleDriverDestroyed(name, state)
            },
            steps,
        })

        this.currentSteps = steps
        this.saveProgress(name, moveToIndex)
        this.driverObj.drive(moveToIndex)
    }

    getActiveIndex() {
        return this.driverObj?.getActiveIndex?.() ?? null
    }

    isActive() {
        return Boolean(this.driverObj)
    }

    setHighlightVisible(visible) {
        document.querySelectorAll('.driver-overlay, .driver-stage, .driver-popover, .hdv-tour-popover').forEach((element) => {
            element.style.visibility = visible ? '' : 'hidden'
        })
    }

    destroy() {
        this.stopAutomacaoModalGuard()
        this.driverObj?.destroy()
        this.driverObj = null
        this.activeTourName = null
    }

    handleDriverDestroyed(name, state) {
        if (this.replacingSteps) {
            this.driverObj = null
            return
        }

        const reason = this.dismissReason
        this.dismissReason = null
        const activeIndex = state?.activeIndex

        if (reason === 'minimize') {
            if (Number.isInteger(activeIndex)) {
                this.saveProgress(name, activeIndex)
            }
        } else if (reason === 'finish' || !isDemoTour(name)) {
            this.clearSavedProgress(name)
            this.clearMinimizedState()
            unmountTourResumeButton()
            this.runTourCleanup()
            this.tourPath = null
        } else {
            this.saveProgress(name, activeIndex)
            this.runTourCleanup()
        }

        this.driverObj = null
        this.activeTourName = null
    }

    handleDoneClick(name, element, step, opts) {
        if (typeof step?.popover?.onDoneClick === 'function') {
            step.popover.onDoneClick(element, step, opts)
            return
        }

        if (typeof step?.popover?.onNextClick === 'function') {
            step.popover.onNextClick(element, step, opts)
            return
        }

        this.finish(name)
    }

    finish(name) {
        this.dismissReason = 'finish'
        this.finishedTourName = name
        this.lastFinishedTourName = name
        this.clearSavedProgress(name)
        this.clearMinimizedState()
        unmountTourResumeButton()
        const hadDriver = Boolean(this.driverObj)
        this.destroy()
        if (!hadDriver) {
            this.runTourCleanup()
            this.tourPath = null
        }
        this.dismissReason = null
        this.finishedTourName = null

        if (name === 'demo-monitorado-demo') {
            document.dispatchEvent(new CustomEvent('hdv-demo-guide-finished'))
        }
    }

    handoff(fromTourName, toTourName, waitSelector, options = {}) {
        this.finish(fromTourName)

        const destinationTourConfig = this.tours[toTourName]
        const elementSelector = waitSelector || destinationTourConfig?.startSelector
        const resumeStepIndex = options.resumeIndex ?? 0

        const startDestinationTour = () =>
            this.start(toTourName, { resumeIndex: resumeStepIndex })

        if (typeof elementSelector === 'string' && document.querySelector(elementSelector)) {
            startDestinationTour()
            return
        }

        if (typeof elementSelector === 'function' && elementSelector()) {
            startDestinationTour()
            return
        }

        if (destinationTourConfig?.startRoute && elementSelector) {
            this.navigateAndWait(destinationTourConfig.startRoute, elementSelector, resumeStepIndex, toTourName)
                .then(startDestinationTour)
                .catch(() => {})
            return
        }

        this.waitForElement(elementSelector)
            .then(startDestinationTour)
            .catch(() => {})
    }

    minimize() {
        const tourName = this.activeTourName
        const activeIndex = this.getActiveIndex()

        if (!isDemoTour(tourName) && Number.isInteger(activeIndex)) {
            this.saveProgress(tourName, activeIndex)
            this.saveMinimizedState(tourName, activeIndex)
        }

        this.dismissReason = 'minimize'
        this.destroy()
        this.syncResumeButton()
    }

    renderMinimizeButton(popover) {
        if (this.activeTourName === 'boas-vindas') return
        if (popover.wrapper.querySelector('.hdv-tour-minimize-btn')) return

        const minimizeButton = document.createElement('button')
        minimizeButton.type = 'button'
        minimizeButton.className = 'hdv-tour-minimize-btn'
        minimizeButton.setAttribute('aria-label', 'Minimizar tour')
        minimizeButton.setAttribute('title', 'Minimizar tour')
        minimizeButton.textContent = '−'
        minimizeButton.addEventListener('click', () => this.minimize())

        popover.wrapper.insertBefore(minimizeButton, popover.closeButton)
    }

    getActiveStep() {
        const activeIndex = this.getActiveIndex()
        return Number.isInteger(activeIndex) ? this.currentSteps?.[activeIndex] : null
    }

    renderFooterActionButton(popover, className, label, onClick) {
        if (popover.wrapper.querySelector(`.${className}`)) return

        const actionButton = document.createElement('button')
        actionButton.type = 'button'
        actionButton.className = className
        actionButton.textContent = label
        actionButton.addEventListener('click', (event) => {
            event.preventDefault()
            event.stopPropagation()
            onClick()
        })

        const footer = popover.footer
        if (footer) {
            footer.classList.add('hdv-tour-footer-with-action')
            const navigation = footer.querySelector('.driver-popover-navigation-btns')
            if (navigation) {
                footer.insertBefore(actionButton, navigation)
            } else {
                footer.appendChild(actionButton)
            }
            return
        }

        popover.wrapper.appendChild(actionButton)
    }

    renderPopoverChrome(popover) {
        this.renderMinimizeButton(popover)

        const step = this.getActiveStep()
        const skipStage = step?.skipStage

        if (skipStage && typeof skipStage.onSkip === 'function') {
            this.renderFooterActionButton(
                popover,
                'hdv-tour-skip-stage-btn',
                skipStage.label || 'Pular etapa',
                skipStage.onSkip
            )
        }

        const nextButton = popover.nextButton
        if (nextButton) {
            const nextLabel = (step?.popover?.nextBtnText || nextButton.textContent || '').trim()
            nextButton.classList.toggle('hdv-tour-finish-next-btn', /^finalizar$/i.test(nextLabel))
        }
    }

    handleClose(name) {
        if (name?.startsWith?.('demo-')) {
            document.dispatchEvent(new CustomEvent('hdv-demo-request-exit'))
            return
        }

        this.finish(name)
    }

    captureStepContext(step, activeIndex) {
        if (!step) return

        const context = {
            path:
                window.location.pathname +
                window.location.search +
                window.location.hash,
            activeTabs: this.getLiveActiveTabs(),
        }
        step._hdvContext = context

        if (Number.isInteger(activeIndex) && this.currentSteps?.[activeIndex]) {
            this.currentSteps[activeIndex]._hdvContext = context
        }

        if (
            Number.isInteger(activeIndex) &&
            this.activeTourName?.startsWith?.('demo-')
        ) {
            try {
                const raw = sessionStorage.getItem(STEP_CONTEXT_STORAGE_KEY)
                const contexts = raw ? JSON.parse(raw) : {}
                contexts[`${this.activeTourName}:${activeIndex}`] = context
                sessionStorage.setItem(
                    STEP_CONTEXT_STORAGE_KEY,
                    JSON.stringify(contexts)
                )
            } catch (error) {}
        }
    }

    async start(name, options = {}) {
        if (!canUseTours()) {
            return
        }

        const config = this.tours[name]

        if (!config) {
            return
        }

        unmountTourResumeButton()
        if (!options.fromMinimizedResume) {
            this.clearMinimizedState()
        }

        try {
            sessionStorage.removeItem(RESUME_STORAGE_KEY)
        } catch (error) {}

        if (this.driverObj && this.activeTourName === name) {
            const nextIndex = options.resumeIndex
            const currentIndex = this.getActiveIndex()
            if (
                Number.isInteger(nextIndex) &&
                nextIndex !== currentIndex
            ) {
                this.destroy()
            } else {
                return
            }
        }

        if (this.driverObj) {
            this.destroy()
        }

        this.activeTourName = name
        this.tourCleanup = null
        this.exportacaoParamModalLock = false
        this.supervisorioModalLock = false

        const savedProgress = options.resumeIndex === undefined
            ? this.getSavedProgress(name)
            : null
        const resumeIndex = options.resumeIndex ?? savedProgress?.resumeIndex
        const startIndex = resumeIndex ?? config.getStartIndex?.() ?? 0

        if (
            !options.fromMinimizedResume &&
            config.startRoute &&
            (resumeIndex === undefined || resumeIndex === 0) &&
            startIndex === 0
        ) {
            const startSelector = config.startSelector || config.startRoute
            const alreadyOnStart = typeof startSelector === 'string'
                ? Boolean(document.querySelector(startSelector))
                : false
            const pathname = window.location.pathname
            const onStartRoute =
                pathname === config.startRoute ||
                pathname.startsWith(`${config.startRoute}/`)

            if (!alreadyOnStart && !onStartRoute) {
                try {
                    await this.navigateAndWait(config.startRoute, startSelector, startIndex, name)
                } catch (error) {
                    return
                }
            } else if (!alreadyOnStart && onStartRoute && typeof startSelector === 'string') {
                try {
                    await this.waitForElement(startSelector)
                } catch (error) {}
            }
        }

        if (config.readySelector && !options.skipReadySelector) {
            try {
                await this.waitForElement(config.readySelector)
            } catch (error) {
                this.syncResumeButton()
                return
            }
        }

        const steps = config.buildSteps(this)
        const stepToStart = steps[startIndex]

        if (typeof stepToStart?.ensureReady === 'function') {
            try {
                await stepToStart.ensureReady(this)
            } catch (error) {}
        }

        this.driverObj = driver({
            showProgress: true,
            allowClose: true,
            animate: true,
            overlayOpacity: 0.65,
            overlayClickBehavior: () => {},
            stagePadding: 6,
            stageRadius: 6,
            popoverClass: 'hdv-tour-popover',
            nextBtnText: 'Próximo',
            prevBtnText: 'Voltar',
            doneBtnText: 'Concluir',
            progressText: '{{current}} de {{total}}',
            onDoneClick: (element, step, opts) => this.handleDoneClick(name, element, step, opts),
            onCloseClick: () => this.handleClose(name),
            onPrevClick: () => this.movePrevious(),
            onPopoverRender: (popover) => this.renderPopoverChrome(popover),
            onHighlighted: (highlightedElement, step, { state, driver: activeDriver }) => {
                this.saveProgress(name, state?.activeIndex)

                if (typeof step?.onHighlighted === 'function') {
                    step.onHighlighted(highlightedElement, step, { state, driver: activeDriver })
                }

                if (typeof step?.ensureReady === 'function') {
                    Promise.resolve(step.ensureReady(this))
                        .then(() => {
                            activeDriver?.refresh?.()
                            this.captureStepContext(step, state?.activeIndex)
                        })
                        .catch(() => {})
                } else {
                    this.captureStepContext(step, state?.activeIndex)
                }
            },
            onDeselected: (deselectedElement, step, deselectOptions) => {
                if (typeof step?.onDeselected === 'function') {
                    step.onDeselected(deselectedElement, step, deselectOptions)
                }
            },
            onDestroyed: (destroyedElement, destroyedStep, { state }) => {
                this.handleDriverDestroyed(name, state)
            },
            steps,
        })

        this.currentSteps = steps
        this.saveProgress(name, startIndex)
        this.tourPath = this.currentPath()
        if (isModalGuardTour(name)) {
            this.startAutomacaoModalGuard()
        }
        this.driverObj.drive(startIndex)
    }
}

const tourManager = new TourManager()

export default tourManager
